/* ============================================================
   redline.ink — shared client/storage type contract

   Cross-references:
   - storage JSON wire shape ↔ docs/api-contract.md
   - crypto wire format ↔ src/crypto/index.ts
   ============================================================ */

/**
 * The well-known author id for the coding agent. The agent is an ordinary
 * thread participant — its messages carry this sentinel id so the reader can
 * style them distinctly (✦ glyph, accent hue) without a separate record type.
 */
export const AGENT_AUTHOR_ID = 'agent'
/** Display name used when the agent sentinel needs a byline. */
export const AGENT_AUTHOR_NAME = 'Agent'

/**
 * One message appended to a comment's thread. Replies are immutable once
 * posted so copied feedback and carried snapshots keep a stable history.
 */
export interface Reply {
  /** `crypto.randomUUID()` — stable reply identity across revisions. */
  id: string
  /** Who wrote it — a human author snapshot, or the agent sentinel. */
  author: { id: string; name: string }
  /** The reply text. */
  note: string
  /** `Date.now()` when posted. */
  createdAt: number
}

/**
 * A single inline review comment anchored into the rendered prose — the head
 * of a thread: the opening `note`/`author` plus any `replies`, either open or
 * `resolved`.
 *
 * Persisted fields are lifted verbatim from the prototype's `createDraft`.
 * Transient UI fields (prefixed `_`) are stripped before persistence — see
 * `cleanTabForSave` in the prototype / `usePersistence`.
 */
export interface Comment {
  /** `crypto.randomUUID()` — stable identity across re-renders. */
  id: string

  /**
   * Source-line span the highlight maps to, used for stable anchoring across
   * re-renders and for the handoff payload's `L<start>-L<end>` references.
   * 1-based, inclusive. Approximated proportionally within a markdown block.
   */
  sourceLineStart: number
  sourceLineEnd: number

  /** The highlighted text, whitespace-collapsed. */
  quotedText: string

  /** Nearest preceding heading text, for the handoff payload's `§ "..."` hint. */
  blockHint: string

  /**
   * Character offsets of the selection within the `.prose` element's text
   * content. Used to rebuild the DOM Range and re-wrap the <mark> on load.
   */
  domRangeStart: number
  domRangeEnd: number

  /** The reviewer's note. Trimmed on commit/save. */
  note: string

  /** `Date.now()` at creation. */
  createdAt: number

  /**
   * The thread: messages appended after the opening note, by any participant
   * (a reviewer or the agent). Ordered chronologically for display and carried
   * across revisions by reply id.
   */
  replies?: Reply[]
  /**
   * Thread status. Absent = open. Set = resolved, with who ({@link
   * AGENT_AUTHOR_ID} or a human author id) and when. Cleared by re-opening.
   */
  resolved?: { at: number; by: string }

  /**
   * Who wrote this comment. `id` is a stable
   * per-browser value (localStorage); `name` is the self-set display name
   * (snapshotted at creation). Color is derived from `id` via `hashColor`, so
   * it stays consistent across browsers and survives a rename. Absent on
   * comments written before author identity existed. A self-asserted label,
   * not a security claim (capability = the link).
   */
  author?: { id: string; name: string }

  // ---- transient UI state (never persisted) ----
  /** True while the comment is an uncommitted draft. */
  _draft?: boolean
  /** True while the comment is being edited in place. */
  _editing?: boolean
  /** Snapshot of `note` taken when editing began, for Cancel restore. */
  _editPrev?: string
  /** Collapsed state for a resolved card. Resolved threads start collapsed to
   *  a single badge row; expanding reveals the quote + note. `undefined` ⇒
   *  defaults to collapsed. */
  _collapsed?: boolean
  /** Render-only: an open thread whose quoted passage can't be found in this
   *  revision. The thread stays open — an explicit status is never overridden
   *  by a failed text search — but with no live words to point at, its card
   *  parks by its section heading and carries a "text changed" notice.
   *  Recomputed on each render. */
  _anchorLost?: boolean
  /** True while the reply composer is open on this thread's card. */
  _replying?: boolean
  /** In-progress reply text (survives card re-renders while composing). */
  _replyDraft?: string
}

/**
 * One open document — a reviewed plan. Keyed by the SHA-256 hex of its
 * markdown (so the same content reopens the same tab + comments). This is
 * also the IndexedDB record shape (`keyPath: 'hash'`).
 */
export interface Tab {
  /** SHA-256 hex of `markdown`. Primary key. */
  hash: string
  /** First 4 hex chars of `hash`, for the tab chip + recent list. */
  shortHash: string
  /** Display name (originating file name, or a derived name for uploads). */
  filename: string
  /** The full markdown source. */
  markdown: string

  /** Inline comments (drafts filtered out on save). */
  comments: Comment[]
  /** Freeform "General comments" from the handoff modal. */
  generalNotes: string
  /** One-line "Summary" verdict from the handoff modal. */
  summary: string

  /** Last scroll position, restored on tab activation. */
  scrollTop: number
  /** Currently-focused comment id (transient; stripped on save). */
  activeCommentId: string | null

  createdAt: number
  updatedAt: number

  /**
   * Original upload time (ms epoch) for a plan opened from a server share —
   * the server record's `createdAt`. Absent for local files, pastes, and
   * portable snapshots. Persists for free (`cleanTabForSave` spreads `{...tab}`).
   */
  publishedAt?: number
}

/**
 * The R2 object body — a JSON wrapper, never raw ciphertext. `version` lets
 * us evolve the wrapper without breaking old clients. The
 * server treats `ciphertext` as opaque forever. Mirrors docs/api-contract.md.
 */
export interface StoredPlan {
  /** Storage schema version. v1. */
  version: 1
  /** base64url of the wire blob: `nonce(12) || ciphertext || tag(16)`. */
  ciphertext: string
  /** Unix seconds at upload. */
  createdAt: number
}

/**
 * The R2 wrapper for an immutable thread snapshot, keyed off the same slug and
 * encrypted with the same plan key. `updatedAt` is retained as the wire field
 * name for compatibility, but the object is created only once.
 */
export interface StoredReview {
  /** Storage schema version. v1. */
  version: 1
  /** base64url of the wire blob: `nonce(12) || ciphertext || tag(16)`. */
  ciphertext: string
  /** Unix seconds at snapshot creation. */
  updatedAt: number
}

/**
 * The immutable thread snapshot an agent may seed beside a revised plan.
 * Human edits to this snapshot remain local until explicitly copied back to
 * the agent. A bare `Comment[]` remains accepted for older links.
 */
export interface ReviewDoc {
  comments: Comment[]
}

/** Success body of `POST /api/new`. */
export interface ApiResponse {
  /** Four BIP39 words joined by `-`, e.g. `quartz-meadow-violin-anchor`. */
  slug: string
}

/** Error body shape for 4xx/5xx responses from the Worker. */
export interface ApiError {
  /** Stable machine-readable code, e.g. `too_large`, `rate_limited`. */
  error: string
  /** Human-readable explanation. */
  message: string
}
